import { createAsyncThunk } from '@reduxjs/toolkit';
import type { EndEvent } from 'jssip/lib/RTCSession';

import { hasSvrsRtPrefix } from 'features/sip/helpers';
import { RtcSessionTerminateCauses, Originator } from 'features/sip/enums';
import type { RootState } from 'features/app/store/store';
import {
  completeTeaming,
  selectIsInFreeSwitchRoomSelector,
} from 'features/teaming/teaming-base/store';
import { forceCancelColdHandoff } from 'features/cold-handoff/store';
import { VriChatEventBus } from 'features/vri-chat/services';
import { sendAnalytics } from 'features/analytics/store';
import { LogLevel } from 'features/analytics/enums';
import { ParticipantsStatusEventBus } from 'features/call/call-status/services';
import { getConnectionChangeTypeByOriginator } from 'features/sip/utils';

export const handleRtcSessionEndedThunk = createAsyncThunk<
  void,
  {
    event: EndEvent;
  },
  { state: RootState }
>(
  'sip/handleRtcSessionEnded',
  async (
    {
      event,
    }: {
      event: EndEvent;
    },
    { getState, dispatch }
  ) => {
    const isRefer = event.cause === RtcSessionTerminateCauses.REFER;
    if (event.originator === Originator.REMOTE) {
      const remoteUri = event.message?.from?.toString();
      const isInFreeSwitchRoom = selectIsInFreeSwitchRoomSelector(getState());

      // When in a free switch room, ignore BYE messages that don't come from svrs-rt prefixed endpoints
      if (isInFreeSwitchRoom && !hasSvrsRtPrefix(remoteUri)) {
        dispatch(
          sendAnalytics({
            Method: 'handleRtcSessionEndedThunk',
            Message:
              'Ignoring BYE message not from svrs-rt endpoint during teaming',
            Level: LogLevel.INFO,
            RemoteUri: remoteUri,
            IsInFreeSwitchRoom: isInFreeSwitchRoom,
          })
        );
        return;
      }
    }

    const isLeaveTeaming =
      event.cause === RtcSessionTerminateCauses.LEAVE_TEAMING;
    const isLeaveHandoff = event.cause === RtcSessionTerminateCauses.HANDOFF;
    const isAdminCanceledCall =
      event.cause === RtcSessionTerminateCauses.ADMIN_CANCELED_CALL;
    const isReturnToQueue =
      event.cause === RtcSessionTerminateCauses.RETURN_TO_QUEUE;
    const isSpecialEndEvent =
      isLeaveTeaming ||
      isLeaveHandoff ||
      isAdminCanceledCall ||
      isReturnToQueue ||
      isRefer;

    const message = event.message?.toString() ?? '';

    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEnded',
        Originator: event.originator,
        Cause: event.cause,
        Message: message,
        Level: LogLevel.INFO,
      })
    );

    if (!isLeaveTeaming && !isRefer) {
      await dispatch(completeTeaming()).unwrap();
    }

    ParticipantsStatusEventBus.deaf.$disconnected.next({
      connectionChangeType: getConnectionChangeTypeByOriginator(
        event.originator
      ),
      isConnectionCallStateNeeded: !isSpecialEndEvent,
    });

    if (!isLeaveHandoff && !isRefer) {
      dispatch(forceCancelColdHandoff());
      VriChatEventBus.$leaveChat.next();
    }
  }
);
